import React from 'react';
import {
  DndContext,
  closestCenter,
  PointerSensor,
  TouchSensor,
  useSensor,
  useSensors,
  DragOverlay,
  UniqueIdentifier,
  DragStartEvent,
  DragEndEvent,
} from '@dnd-kit/core';
import {
  SortableContext,
  useSortable,
  arrayMove,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

interface SortableItemWrapperProps {
  id: string;
  children: React.ReactNode;
}

const SortableItemWrapper = ({ id, children }: SortableItemWrapperProps) => {
  const { setNodeRef, transform, transition, listeners, attributes, isDragging } =
    useSortable({ id });

  const style: React.CSSProperties = {
    transform: isDragging ? undefined : CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0 : 1,
  };

  return (
    <div ref={setNodeRef} style={style} className="sortable-item">
      {React.cloneElement(children as React.ReactElement<{ dragHandleProps?: unknown }>, {
        dragHandleProps: { listeners, attributes },
      })}
    </div>
  );
};

export interface SortableItem {
  id: string;
}

export interface SortableListProps<T extends SortableItem> {
  items: T[];
  onOrderChange: (newItems: T[], newIds: string[]) => void;
  renderItem: (item: T, isDragOverlay?: boolean) => React.ReactNode;
}

export const SortableList = <T extends SortableItem>({
  items,
  onOrderChange,
  renderItem,
}: SortableListProps<T>) => {
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: { distance: 8 },
    }),
    useSensor(TouchSensor, {
      activationConstraint: { delay: 200, tolerance: 5 },
    }),
  );
  const [activeId, setActiveId] = React.useState<UniqueIdentifier | null>(null);

  const handleDragStart = (event: DragStartEvent) => {
    setActiveId(event.active.id);
  };

  const handleDragCancel = () => {
    setActiveId(null);
  };

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    setActiveId(null);

    if (!over || active.id === over.id) return;

    const oldIndex = items.findIndex((item) => item.id === active.id);
    const newIndex = items.findIndex((item) => item.id === over.id);

    const newOrder = arrayMove(items, oldIndex, newIndex);
    onOrderChange(
      newOrder,
      newOrder.map((item) => item.id),
    );
  };

  const activeItem = items.find((item) => item.id === activeId);

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCenter}
      onDragStart={handleDragStart}
      onDragCancel={handleDragCancel}
      onDragEnd={handleDragEnd}
    >
      <SortableContext
        items={items.map((item) => item.id)}
        strategy={verticalListSortingStrategy}
      >
        <div className="sortable-list">
          {items.map((item) => (
            <SortableItemWrapper key={item.id} id={item.id}>
              {renderItem(item, false)}
            </SortableItemWrapper>
          ))}
        </div>
        <DragOverlay>{activeItem ? renderItem(activeItem, true) : null}</DragOverlay>
      </SortableContext>
    </DndContext>
  );
};
